import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { BoardSearchSimplePickerPanel } from '../../board/board-search-filters/BoardSearchSimplePickerPanel';
import { computeToolbarFilterPopoverLayout } from '../../board/board-search-filters/toolbarFilterPopoverLayout';

const PICKER_WIDTH = 120;
const PICKER_MAX_HEIGHT = 220;

export default function CxEmailOptionPicker({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Selecionar',
  ariaLabel,
  className = '',
}) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [layout, setLayout] = useState(null);

  const items = options.map((opt) =>
    typeof opt === 'string' ? { value: opt, label: opt } : { value: opt.value, label: opt.label }
  );

  const selectedLabel = items.find((i) => i.value === value)?.label ?? placeholder;

  const updateLayout = useCallback(() => {
    const el = anchorRef.current;
    if (!el || !open) {
      setLayout(null);
      return;
    }
    const rect = el.getBoundingClientRect();
    setLayout(computeToolbarFilterPopoverLayout(rect, PICKER_WIDTH, PICKER_MAX_HEIGHT));
  }, [open]);

  useLayoutEffect(() => {
    updateLayout();
  }, [open, items.length, updateLayout]);

  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => updateLayout();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, updateLayout]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      const t = event.target;
      if (anchorRef.current?.contains(t)) return;
      if (t.closest?.('.board-search-filters-popover')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  return (
    <>
      <div className={`cx-email-option-picker ${className}`.trim()} ref={anchorRef}>
        {label ? <span className="cx-email-option-picker-label">{label}</span> : null}
        <button
          type="button"
          className={`cx-email-option-picker-trigger${value == null || value === '' ? ' cx-email-option-picker-trigger--empty' : ''}`}
          aria-label={ariaLabel || label || 'Selecionar opção'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span>{selectedLabel}</span>
          <ChevronDown size={11} strokeWidth={1.75} className={open ? 'is-open' : ''} aria-hidden />
        </button>
      </div>
      {open && layout
        ? createPortal(
            <div
              className="board-search-filters-popover board-search-filters-popover--simple cx-email-option-picker-popover"
              style={{
                position: layout.position,
                top: layout.top,
                left: layout.left,
                width: layout.width,
                boxSizing: layout.boxSizing,
                zIndex: 13000,
                maxHeight: layout.maxHeight,
                '--bsf-cidade-popover-max-h': layout['--bsf-cidade-popover-max-h'],
                transform: layout.flipped ? 'translateY(-100%)' : undefined,
              }}
              role="dialog"
              aria-label={ariaLabel || label || 'Opções'}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <BoardSearchSimplePickerPanel
                showSearch={false}
                items={items}
                getKey={(i) => i.value}
                getLabel={(i) => i.label}
                selectedKeys={value == null || value === '' ? [] : [value]}
                onToggle={(item) => {
                  onChange(item.value);
                  setOpen(false);
                }}
                emptyMessage="Sem opções"
                listAriaLabel={ariaLabel || label || 'Opções'}
              />
            </div>,
            document.body
          )
        : null}
    </>
  );
}
